import { N_DASH } from '@monoimin/constants';

import type {
  ByLocales,
  Locales,
  StrapiEstateCurrency,
} from '@monoimin/sb-types';

interface FormatPropertyCostArgs {
  costFrom?: number | string;
  costTo?: number | string;
  currency?: StrapiEstateCurrency;
  locale: Locales;
}

const fromText: ByLocales<Locales, string> = {
  en: 'From',
  ru: 'От',
  de: 'Von',
  tr: 'Den',
  es: 'De',
};

export function formatPropertyCost({
  locale,
  costFrom,
  costTo,
  currency: propsCurrency,
}: FormatPropertyCostArgs): string {
  if (!costFrom) {
    return '';
  }

  const currency = propsCurrency?.toUpperCase() || 'EUR';

  try {
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(0);
  } catch (e) {
    console.error(`Invalid currency code: ${currency}`);
    return '';
  }

  const formatter = new Intl.NumberFormat(locale, {
    currency,
    style: 'currency',
    minimumFractionDigits: 0,
    useGrouping: true,
  });

  const numCostFrom = +costFrom;
  const numCostTo = costTo ? +costTo : null;

  if (Number.isNaN(numCostFrom)) {
    console.error('Invalid costFrom value:', costFrom);
    return '';
  }

  if (costTo && Number.isNaN(numCostTo!)) {
    console.error('Invalid costTo value:', costTo);
    return '';
  }

  const formattedFrom = formatter.format(+costFrom);
  if (!costTo) {
    if (locale === 'ru') {
      return `${fromText[locale]} ${formattedFrom}`;
    }

    return `${formattedFrom}+`;
  }

  if (numCostFrom === numCostTo) {
    return formattedFrom;
  }

  if (numCostTo && numCostFrom > numCostTo) {
    console.error('costFrom is greater than costTo:', { costFrom, costTo });
    return 'Invalid cost';
  }

  return `${formattedFrom} ${N_DASH} ${formatter.format(+costTo)}`;
}

export default formatPropertyCost;
